import { useEffect, useState, type ReactNode } from "react";

import type { SavedView } from "../api";
import iconUrl from "../assets/Watchpoint_Icon.png";
import { ChargesIcon, CollapseIcon, DriversIcon, OverviewIcon } from "../icons";

export type SidebarMode = "expanded" | "rail";

const SIDEBAR_KEY = "watchpoint.sidebar";

/** Storage may be absent or blocked (private mode, enterprise policy). */
export function readSidebarPref(): SidebarMode {
  try {
    return window.localStorage.getItem(SIDEBAR_KEY) === "rail"
      ? "rail"
      : "expanded";
  } catch {
    return "expanded";
  }
}

export function storeSidebarPref(mode: SidebarMode): void {
  try {
    window.localStorage.setItem(SIDEBAR_KEY, mode);
  } catch {
    // Preference lives only for this session.
  }
}

/** Between the top-bar breakpoint and the wide layout, the rail is forced. */
export function useRailForced(): boolean {
  const [forced, setForced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia(
      "(min-width: 48rem) and (max-width: 74.9375rem)",
    );
    const update = () => {
      setForced(query.matches);
    };
    update();
    query.addEventListener("change", update);
    return () => {
      query.removeEventListener("change", update);
    };
  }, []);
  return forced;
}

const links: Array<{ href: string; label: string; icon: ReactNode }> = [
  { href: "#overview", label: "Overview", icon: <OverviewIcon size={16} /> },
  { href: "#drivers", label: "Cost drivers", icon: <DriversIcon size={16} /> },
  { href: "#charges", label: "Charges", icon: <ChargesIcon size={16} /> },
];

export function Sidebar({
  mode,
  views,
  onToggle,
}: {
  mode: SidebarMode;
  views: SavedView[];
  onToggle: () => void;
}) {
  const rail = mode === "rail";
  return (
    <aside
      className={rail ? "sidebar sidebar--rail" : "sidebar"}
      aria-label="Primary navigation"
    >
      <div className="brand">
        <img className="brand__mark" src={iconUrl} alt="" />
        {!rail && <span>Watchpoint</span>}
      </div>
      <nav>
        {links.map((link, index) => (
          <a
            key={link.href}
            className={index === 0 ? "nav-link nav-link--active" : "nav-link"}
            href={link.href}
            aria-label={link.label}
            title={rail ? link.label : undefined}
          >
            {link.icon}
            {!rail && <span>{link.label}</span>}
          </a>
        ))}
      </nav>
      {!rail && (
        <div className="sidebar__saved">
          <p className="eyebrow">Saved views</p>
          {views.length === 0 ? (
            <p className="muted">No saved views</p>
          ) : (
            views
              .slice(0, 4)
              .map((view) => <span key={view.id}>{view.name}</span>)
          )}
        </div>
      )}
      <button
        className="sidebar__toggle icon-button"
        type="button"
        onClick={onToggle}
        aria-label={rail ? "Expand sidebar" : "Collapse sidebar"}
        title={rail ? "Expand sidebar" : "Collapse sidebar"}
      >
        <CollapseIcon size={16} />
      </button>
    </aside>
  );
}
